<template>
  <div :id="contentId" class="map">
    <div class="loading-overlay" v-if="isLoading">
      <img src="/frontend/img/loading-spinner.svg" />
    </div>
    <l-map v-if="ready"
      ref="map"
      :center="center"
      :zoom="zoom"
      map-type-id="terrain"
      class="full-screen"
      @update:center="handleCenterChanged"
    >
      <v-tilelayer-google-maps
        :apikey="apiKey"
        :options="{
          type: 'roadmap',
          styles: MapConfig[appearance.toLowerCase()]
        }"
      />

      <v-marker
        v-if="myGeometry"
        :lat-lng="[
          Number(myGeometry.location.lat),
          Number(myGeometry.location.lng)
        ]"
        :clickable="true"
        :icon="getMyGeometryMarkerIcon()"
      />

      <v-marker-cluster
        :options="{
          iconCreateFunction: getClusterIcon,
          maxClusterRadius: 60,
          disableClusteringAtZoom: 8,
          spiderfyOnMaxZoom: false
        }"
        ref="clusterRef"
      >
        <v-marker
          v-for="(m, index) in models"
          v-bind:key="m.id + '-' + m.marker_color_key"
          :lat-lng="[Number(m.address.latitude), Number(m.address.longitude)]"
          :clickable="true"
          @click="onMarkerPress(m)"
          :options="{
            vehicle_count: getVehicleCount(m)
          }"
          :icon="getDepotMarkerIcon(m)"
        />
      </v-marker-cluster>
    </l-map>

    <div class="legend" v-if="showLegend">
      <div class="arrow">
        <i class="cs-arrow-right"></i>
      </div>
      <label>{{ i18n.app.map.legend }}</label>
      <ul>
        <li
          v-for="markerColor in markerColors"
          :class="markerColor.key"
          v-bind:key="markerColor.key"
        >
          <span></span>
          {{ markerColor.text }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { latLng, DivIcon } from "leaflet";
import { LMap, LTileLayer, LMarker, LIcon } from "vue2-leaflet";
import Vue2LeafletGoogleMutant from "@/plugin/leaflet/Vue2LeafletGoogleMutant";
import Vue2LeafletMarkerCluster from "vue2-leaflet-markercluster";
import MapConfig from "./MapConfig";
import { Appearance } from "@/models/UserPreference";
import { MarkerColorKey } from "@/models/MarkerColor";
import { mapGetters } from "vuex";
import Depot from "@/models/Depot";
import { loadGoogleMapsScript } from "@/utils/googleMapsLoader";

const MAX_ZOOM = 13;
export default {
  props: {
    models: Object | Array,
    markerColors: Array,
    myGeometry: Object,
    isLoading: Boolean,
    showLegend: Boolean,
    contentId: {
      type: String,
      default: "content"
    }
  },
  components: {
    "l-map": LMap,
    "v-marker": LMarker,
    "v-tilelayer": LTileLayer,
    "v-icon": LIcon,
    "v-tilelayer-google-maps": Vue2LeafletGoogleMutant,
    "v-marker-cluster": Vue2LeafletMarkerCluster
  },
  data() {
    return {
      MapConfig: MapConfig,
      apiKey: config.google.api_key_frontend,
      zoom: 9,
      center: [52.5, 5.5],
      Appearance: Appearance,
      bounds: null,
      MarkerColorKey: MarkerColorKey,
      selectedDepot: null,
      ready: false
    };
  },
  mounted() {
    $(".legend").click(function() {
      $(this).toggleClass("open", 1000);
    });

    loadGoogleMapsScript(config.google.api_key_frontend)
      .then(() => {
        this.ready = true;
      })
      .catch(error => console.error("Google Maps failed to load", error));
  },
  beforeDestroy() {
    try {
      MapState.save(this.getBounds(), null, []);
    } catch (e) {}
  },
  methods: {
    loadGoogleMapsScript() {
      return new Promise((resolve, reject) => {
        if (window.google && window.google.maps) {
          resolve();
          return;
        }

        const script = document.createElement("script");
        let scriptUrl =
          "https://maps.googleapis.com/maps/api/js?key=" + config.google.api_key_frontend;

        scriptUrl += this.lang ? "&language=" + this.lang : "";
        scriptUrl += this.region ? "&region=" + this.region : "";

        script.src = scriptUrl;

        script.onload = () => {
          console.log("Google Maps API loaded.");
          resolve();
        };
        script.onerror = () => {
          reject("Google Maps failed to load.");
        };

        document.head.appendChild(script);
      });
    },
    highlightMarkers(models_focused) {
      this.$forceUpdate();
    },
    refreshClusters() {
      if (this.$refs.clusterRef && this.$refs.clusterRef.mapObject) {
        this.$refs.clusterRef.mapObject.refreshClusters();
      }
    },
    onMarkerPress(depot) {
      this.selectedDepot = depot;

      this.panTo({
        lat: Number(depot.address.latitude),
        lng: Number(depot.address.longitude)
      });

      this.$forceUpdate();

      this.$emit("onMarkerPress", depot);
    },
    getMyGeometryMarkerIcon(depot) {
      return new DivIcon({
        className: "my-div-icon",
        iconSize: [24, 30.5],
        html: `
        <div class="cs-marker me"></div>`
      });
    },
    getDepotMarkerIcon(depot) {
      return new DivIcon({
        className: "my-div-icon",
        iconSize: [24, 30.5],
        html: `
        <div class="cs-marker ${this.getDepotIconColor(depot)} ${
          depot?.state?.selected ||
          (this.selectedDepot && this.selectedDepot.id === depot.id)
            ? "animated bounce"
            : ""
        } ${
          depot?.state?.focused === false && depot?.state?.selected === false
            ? "empty"
            : ""
        }">
          ${this.getVehicleCountText(depot)}
        </div>`
      });
    },
    getClusterIcon(cluster) {
      const markers = cluster.getAllChildMarkers();
      var count = 0;

      markers.map(item => (count += item.options.vehicle_count));

      return new DivIcon({
        html: `
        <div class="cs-cluster blue ${count == 0 ? "empty" : ""}">
          <span>${count}</span>
        </div>`,
        className: "marker-cluster",
        iconSize: [40, 40]
      });
    },
    panTo(coords, userZoom) {
      const minZoom = userZoom || 10;
      const zoom = this.$refs.map.mapObject.getZoom();

      if (zoom < minZoom) {
        this.$refs.map.mapObject.flyTo(coords, minZoom, {
          animated: true,
          easeLinearity: 1,
          duration: 0.75
        });
      } else {
        this.$refs.map.mapObject.panTo(coords, { animated: true });
      }
    },
    fitBounds(latlngArr = []) {
      return this.$refs?.map?.mapObject?.fitBounds(latlngArr);
    },
    getBounds() {
      if (!this.$refs.map?.mapObject) return;

      return this.$refs?.map?.mapObject?.getBounds();
    },
    getCenter(coords) {
      if (this.$refs.map.mapObject) {
        return this.$refs?.map?.mapObject?.getCenter();
      }
    },
    handleCenterChanged() {
      // MapState.save(this.getBounds(), null, []);
    },
    getVehicleCount(m) {
      if (!m?.state) return 1;
      return m?.state?.available_vehicles
        ? m?.state?.available_vehicles?.length
        : 0;
    },
    getVehicleCountText(m) {
      return m?.state
        ? `<span>${m?.state?.available_vehicles?.length}</span>`
        : "";
    },
    getDepotIconColor(depot) {
      if (this.selectedDepot && this.selectedDepot.id === depot.id)
        return MarkerColorKey.RED;
      if (!depot?.state) return "blue";
      return depot?.state?.available_vehicles?.length > 0
        ? depot.marker_color?.key || "blue"
        : MarkerColorKey.RED;
    }
  },
  computed: {
    ...mapGetters({
      appearance: "appearance"
    })
  }
};

export class MapState {
  static key = "map_state";

  static save(bounds, depotIdSelected, depotIdsFocused) {
    sessionStorage.setItem(
      MapState.key,
      JSON.stringify({
        bounds: bounds,
        depot_id_selected: depotIdSelected,
        depot_ids_focused: depotIdsFocused
      })
    );
  }

  static load() {
    let state = sessionStorage.getItem(MapState.key);

    if (!state) return;

    state = JSON.parse(state);
    state = {
      ...state,
      depot_id_selected: state.depot_id_selected,
      depot_ids_focused: state.depot_ids_focused
    };

    return state;
  }

  static clear() {
    return sessionStorage.removeItem(MapState.key);
  }
}
</script>

<style scoped>
@import "~leaflet.markercluster/dist/MarkerCluster.css";
@import "~leaflet.markercluster/dist/MarkerCluster.Default.css";
</style>
